import { Inject, Injectable, NgZone } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from '@core/services/auth.service';
import { ApiService } from '@core/services/api.service';
import { LoggerService } from '@core/services/logger.service';
import { MESSAGES_STREAM_PLATFORM, MessagesStreamPlatform } from './messages-stream-platform';
import { createSseEventParser } from './sse-event-parser';

const STREAM_ENDPOINT = 'communication/messages/unread-count/stream';
const BACKOFF_FIRST_BASE_MS = 1000;
const BACKOFF_MAX_BASE_MS = 30000;

/** Compares the media type only: `text/event-stream; charset=utf-8` is an event stream too. */
function isEventStream(contentType: string | null): boolean {
  return (contentType ?? '').split(';')[0].trim().toLowerCase() === 'text/event-stream';
}

/** What the stream does with a response (D11). */
export type StreamResponseOutcome = 'read' | 'renew' | 'failure' | 'stop';

/**
 * D11: a 200 event stream is read; a 401 asks for a renewal; 408, 429, 5xx and a 200 of another
 * media type are failures that reopen with backoff; any other status stops — a client error does
 * not fix itself, and reopening would only spend the user's rate limit. A 200 that is read but
 * never reaches an established connection becomes a failure while being read, not here.
 */
export function classifyStreamResponse(status: number, contentType: string | null): StreamResponseOutcome {
  if (status === 200) {
    return isEventStream(contentType) ? 'read' : 'failure';
  }
  if (status === 401) {
    return 'renew';
  }
  if (status === 408 || status === 429 || (status >= 500 && status <= 599)) {
    return 'failure';
  }
  return 'stop';
}

/**
 * AC9: the wait before reopening after the `failureNumber`-th consecutive failure. The base doubles
 * from 1 s up to 30 s; the wait is drawn uniformly in [base/2, base) from `random` in [0, 1).
 */
export function computeBackoffDelay(failureNumber: number, random: number): number {
  const base = Math.min(BACKOFF_MAX_BASE_MS, BACKOFF_FIRST_BASE_MS * 2 ** (failureNumber - 1));
  return Math.floor(base / 2 + (random * base) / 2);
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * One connection cycle: born in connect(), discarded by disconnect(). Everything that can still
 * happen later (the request, each read, a scheduled reopening, a pending renewal) belongs to the
 * cycle it was started in, and is dropped once that cycle is no longer the current one.
 */
interface ConnectionCycle {
  readonly abortController: AbortController;
  /** Cancels what the cycle left pending; disconnect() runs and clears them. */
  readonly cancellers: Set<() => void>;
  /** Failures since the last established connection; only an established connection resets it (D10). */
  consecutiveFailures: number;
  /** One renewal per 401 until an established connection gives the right back (AC12). */
  renewalAvailable: boolean;
}

/**
 * The unread-count push over SSE, read with `fetch` so the session token travels in the
 * `Authorization` header — never in the URL, which the API no longer accepts.
 *
 * The request does not go through the `HttpClient` interceptors, so the headers they would add are
 * set here, on purpose limited to the ones the endpoint needs: no `X-Culture`, no workstation
 * headers. Network, reading and timers run outside the Angular zone (a never-ending `fetch` would
 * keep the app unstable, and the service worker registers only once it is stable); only the
 * emission and the renewal return to it, so the header updates without manual change detection and
 * the session redirect the renewal may do runs where the router expects it.
 *
 * Reopening is the service's own policy, not the `HttpClient` 429 retry: a failure reopens with
 * exponential backoff and jitter, a 401 renews the token once, and only an established connection —
 * a 200 event stream whose first complete line was read — counts as success.
 *
 * No log line receives the token, the headers or the `fetch` init — only reasons and numbers.
 */
@Injectable({ providedIn: 'root' })
export class MessagesRealtimeService {
  private cycle: ConnectionCycle | null = null;
  private readonly unreadCountChangedSubject = new Subject<void>();
  readonly unreadCountChanged$: Observable<void> = this.unreadCountChangedSubject.asObservable();

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private logger: LoggerService,
    private ngZone: NgZone,
    @Inject(MESSAGES_STREAM_PLATFORM) private platform: MessagesStreamPlatform
  ) {}

  /**
   * Starts a connection cycle. While a cycle exists — requesting, reading, waiting to reopen or
   * stopped — this is a no-op: a stop holds until disconnect() discards the cycle and a new
   * connect() starts another, with the backoff and the right to a renewal reset.
   */
  connect(): void {
    if (this.cycle) {
      return;
    }
    const cycle: ConnectionCycle = {
      abortController: new AbortController(),
      cancellers: new Set(),
      consecutiveFailures: 0,
      renewalAvailable: true,
    };
    this.cycle = cycle;
    this.ngZone.runOutsideAngular(() => void this.open(cycle, false));
  }

  /**
   * Discards the current cycle: aborts its request and cancels the scheduled reopening and the
   * subscription to a pending renewal. The cancellation is not a failure.
   */
  disconnect(): void {
    const cycle = this.cycle;
    if (!cycle) {
      return;
    }
    this.cycle = null;
    cycle.abortController.abort();
    cycle.cancellers.forEach((cancel) => cancel());
    cycle.cancellers.clear();
  }

  private buildHeaders(token: string): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream',
      // Keeps the service worker away from a response that never ends.
      'ngsw-bypass': 'true',
    };
    // Same rule as the HTTP interceptor: a blank stored id is no id.
    const sessionId = this.platform.readSessionId()?.trim();
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    return headers;
  }

  /**
   * Sends one request of `cycle`, reads it until it ends, and acts on the outcome by D11. Every
   * continuation checks the cycle is still the current one, and drops its result otherwise. Nothing
   * here rejects: an error while preparing or sending the request is a failure like the network's.
   *
   * @param isReopen whether the request reopens the cycle's stream, rather than being the opening
   *   made by connect(); only a reopening emits when it reaches an established connection (AC14).
   */
  private async open(cycle: ConnectionCycle, isReopen: boolean): Promise<void> {
    try {
      // Read at the moment of each request and never kept: a renewed token is picked up by the next one.
      const token = this.authService.getToken();
      if (!token) {
        this.stop('sem token');
        return;
      }
      const url = this.apiService.buildPublicUrl(STREAM_ENDPOINT);
      const init: RequestInit = {
        headers: this.buildHeaders(token),
        signal: cycle.abortController.signal,
        cache: 'no-store',
      };
      const response = await this.platform.fetch(url, init);
      if (cycle !== this.cycle) {
        return;
      }
      const outcome = classifyStreamResponse(response.status, response.headers.get('Content-Type'));
      if (outcome !== 'read' || response.body === null) {
        // Nothing will read it: give the body up instead of leaving the connection to drain.
        response.body?.cancel().catch(() => undefined);
        this.handleUnreadResponse(cycle, outcome, String(response.status));
        return;
      }
      await this.read(cycle, response.body, isReopen);
      if (cycle === this.cycle) {
        // Established or not, a body that ends is a failure; only the count it continues differs.
        this.fail(cycle, 'fim do corpo');
      }
    } catch (error) {
      // A cancellation — disconnect() aborting this cycle, or any AbortError — is not a failure.
      if (cycle !== this.cycle || isAbortError(error)) {
        return;
      }
      this.fail(cycle, 'rede');
    }
  }

  /**
   * Reads the body until it ends or `cycle` is discarded, emitting once per message event. The
   * connection is established once its first complete line has been read, in whichever chunk: that
   * resets the failure count, gives the right to a renewal back and, on a reopening, emits once
   * before the events of the same chunk (D6, D10).
   */
  private async read(cycle: ConnectionCycle, body: ReadableStream<Uint8Array>, isReopen: boolean): Promise<void> {
    const reader = body.getReader();
    // `stream: true` keeps a multi-byte character split between chunks whole.
    const decoder = new TextDecoder('utf-8');
    const parser = createSseEventParser();
    let established = false;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        return;
      }
      if (cycle !== this.cycle) {
        reader.cancel().catch(() => undefined);
        return;
      }
      const { completedLines, messageEvents } = parser.push(decoder.decode(value, { stream: true }));
      if (!established && completedLines > 0) {
        established = true;
        cycle.consecutiveFailures = 0;
        cycle.renewalAvailable = true;
        if (isReopen) {
          this.emit();
        }
      }
      for (let i = 0; i < messageEvents; i++) {
        this.emit();
      }
    }
  }

  private emit(): void {
    this.ngZone.run(() => this.unreadCountChangedSubject.next());
  }

  /** Acts on a response that will not be read, as classified by D11. */
  private handleUnreadResponse(cycle: ConnectionCycle, outcome: StreamResponseOutcome, status: string): void {
    if (outcome === 'renew') {
      this.renew(cycle);
    } else if (outcome === 'stop') {
      this.stop(status);
    } else {
      // A failure, or a 200 event stream with no body to read: no established connection either way.
      this.fail(cycle, status);
    }
  }

  /**
   * A 401: one renewal through the app's shared `refreshAccessToken()`, then one reopening right
   * away, with no backoff. Leaves the failure count alone (AC12). The service never renews on its
   * own initiative and never stores the token it gets.
   */
  private renew(cycle: ConnectionCycle): void {
    if (!cycle.renewalAvailable) {
      this.stop('401 depois da renovação');
      return;
    }
    cycle.renewalAvailable = false;
    this.logger.warn('MessagesRealtimeService: renewing the token after a 401');
    // disconnect() unsubscribes, so a renewal that settles after it never reaches `onRenewal` — that
    // is the cycle guard here. It drops only this listener: the shared renewal goes on for the others.
    const subscription = new Subscription();
    const cancel = (): void => subscription.unsubscribe();
    cycle.cancellers.add(cancel);
    const onRenewal = (renewed: boolean): void => {
      cycle.cancellers.delete(cancel);
      if (renewed) {
        this.reopen(cycle);
      } else {
        this.stop('renovação falhou');
      }
    };
    // Inside the zone: the renewal updates the session state and may redirect to the login.
    this.ngZone.run(() =>
      subscription.add(
        this.authService
          .refreshAccessToken()
          .pipe(take(1))
          .subscribe({ next: onRenewal, error: () => onRenewal(false) })
      )
    );
  }

  /** Reopens outside the zone, whatever zone the timer or the renewal settled in. */
  private reopen(cycle: ConnectionCycle): void {
    this.ngZone.runOutsideAngular(() => void this.open(cycle, true));
  }

  /** Counts one more consecutive failure and schedules the reopening after its backoff. */
  private fail(cycle: ConnectionCycle, cause: string): void {
    cycle.consecutiveFailures++;
    const failure = cycle.consecutiveFailures;
    const delayMs = computeBackoffDelay(failure, this.platform.random());
    this.logger.warn('MessagesRealtimeService: stream reopening scheduled', { failure, delayMs, cause });
    const cancel = this.platform.schedule(() => {
      cycle.cancellers.delete(cancel);
      if (cycle === this.cycle) {
        this.reopen(cycle);
      }
    }, delayMs);
    cycle.cancellers.add(cancel);
  }

  private stop(reason: string): void {
    this.logger.warn('MessagesRealtimeService: stream stopped', { reason });
  }
}
